import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Lock, Headphones, Clock, Trophy, Share2 } from "lucide-react";
import FinishedDialog from "@/components/reader/FinishedDialog";
import { BOOKS } from "@/lib/catalog";
import { formatDuration } from "@/lib/reader";
import StreakBadge from "@/components/reader/StreakBadge";

const SLOTS = ["survival", "youre-never-safe", "suspicious-times", "dingo"];

function BookProgress({ page, total, stats, slot }) {
  const pct = total ? Math.min(100, Math.floor((page / total) * 100)) : 0;
  return (
    <div className="mt-3" data-testid={`library-progress-${slot}`}>
      <div className="h-1 bg-white/10"><div className="h-1 bg-primary-gold transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
      <div className="flex flex-wrap justify-between gap-x-3 gap-y-1 mt-2 font-mono-ui text-[10px] uppercase tracking-[0.15em] text-soft">
        <span data-testid={`library-progress-pct-${slot}`}>{pct}% through</span>
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1" data-testid={`library-read-time-${slot}`}><Clock className="w-3 h-3" /> {formatDuration(stats?.read)}</span>
          <span className="flex items-center gap-1" data-testid={`library-listen-time-${slot}`}><Headphones className="w-3 h-3" /> {formatDuration(stats?.listen)}</span>
        </span>
      </div>
    </div>
  );
}

function LibraryCard({ slot, book, state, onOpen, onShare }) {
  const meta = BOOKS.find((b) => b.id === slot);
  const isOpen = book && state.current_book_id === book.id;
  const saved = book ? state.positions?.[book.id] : null;
  return (
    <div className="group" data-testid={`library-book-${slot}`}>
      <button
        disabled={!book}
        onClick={() => onOpen(book)}
        className="relative block w-full aspect-[2/3] overflow-hidden border border-white/10 disabled:cursor-not-allowed"
      >
        <img
          src={meta.cover}
          alt={meta.fullTitle}
          className={`w-full h-full object-cover transition-transform duration-700 ${book ? "group-hover:scale-105" : "grayscale opacity-40"}`}
        />
        {isOpen && (
          <span className="absolute bottom-3 left-3 bg-primary-gold text-[#0B0C10] font-mono-ui text-[10px] uppercase tracking-[0.2em] px-2.5 py-1">
            Open now
          </span>
        )}
        {book && state.finished?.[book.id] && (
          <span className="absolute top-3 right-3 flex items-center gap-1 bg-[#0B0C10]/85 border border-primary-gold/60 text-primary-gold font-mono-ui text-[10px] uppercase tracking-[0.2em] px-2.5 py-1" data-testid={`library-finished-${slot}`}>
            <Trophy className="w-3 h-3" /> Finished
          </span>
        )}
        {!book && (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex items-center gap-2 bg-[#0B0C10]/80 border border-white/15 px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-soft">
              <Lock className="w-3 h-3" /> Coming soon
            </span>
          </span>
        )}
      </button>
      <h3 className="font-serif-display text-xl mt-4 leading-tight">{meta.fullTitle}</h3>
      {book && (
        <button
          onClick={() => onOpen(book)}
          data-testid={`library-open-${slot}`}
          className="mt-3 inline-flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em] text-primary-gold hover:text-text-primary transition-colors"
        >
          <BookOpen className="w-4 h-4" />
          {saved && saved > 1 ? `Continue · p. ${saved} of ${book.page_count}` : `Start reading · ${book.page_count} pages`}
        </button>
      )}
      {book && <BookProgress page={saved || 0} total={book.page_count} stats={state.stats?.[book.id]} slot={slot} />}
      {book && state.finished?.[book.id] && (
        <button onClick={() => onShare(slot, state.finished[book.id])} data-testid={`library-share-finished-${slot}`} className="mt-3 inline-flex items-center gap-2 font-mono-ui text-[10px] uppercase tracking-[0.18em] text-soft hover:text-primary-gold transition-colors">
          <Share2 className="w-3.5 h-3.5" /> Share my "I finished" card
        </button>
      )}
    </div>
  );
}

export default function ReaderLibrary({ books, state, userName }) {
  const navigate = useNavigate();
  const [sharing, setSharing] = useState(null);
  const finishedSlots = SLOTS.filter((s) => {
    const b = books.find((x) => x.product_id === s);
    return b && state.finished?.[b.id];
  });
  const completedAt = finishedSlots.length === SLOTS.length
    ? finishedSlots.map((s) => state.finished[books.find((x) => x.product_id === s).id]).sort().pop()
    : null;
  const totals = Object.values(state.stats || {}).reduce((t, s) => ({ read: t.read + (s.read || 0), listen: t.listen + (s.listen || 0) }), { read: 0, listen: 0 });
  return (
    <div data-testid="reader-library">
      {completedAt && (
        <button onClick={() => setSharing({ productId: "dingo", finishedAt: completedAt, completionist: true })} data-testid="library-completionist" className="w-full mb-8 flex items-center justify-between gap-4 border border-primary-gold/50 bg-primary-gold/10 px-6 py-5 text-left hover:bg-primary-gold/15 transition-colors">
          <span className="flex items-center gap-4">
            <Trophy className="w-7 h-7 text-primary-gold" />
            <span>
              <span className="block font-serif-display text-2xl">Leckie Completionist</span>
              <span className="block text-soft text-sm">You've finished all four novels — share your card.</span>
            </span>
          </span>
          <Share2 className="w-5 h-5 text-primary-gold shrink-0" />
        </button>
      )}
      {(totals.read > 0 || totals.listen > 0) && (
        <div className="flex flex-wrap items-center gap-x-10 gap-y-5 mb-10 border-l-2 border-primary-gold pl-5" data-testid="library-stats-summary">
          <StreakBadge days={state.active_days} />
          <p><span className="block font-mono-ui text-[10px] uppercase tracking-[0.22em] text-soft">Time reading</span><span className="font-serif-display text-3xl" data-testid="library-total-read">{formatDuration(totals.read)}</span></p>
          <p><span className="block font-mono-ui text-[10px] uppercase tracking-[0.22em] text-soft">Time listening</span><span className="font-serif-display text-3xl" data-testid="library-total-listen">{formatDuration(totals.listen)}</span></p>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
        {SLOTS.map((slot) => (
          <LibraryCard
            key={slot}
            slot={slot}
            book={books.find((b) => b.product_id === slot)}
            state={state}
            onOpen={(b) => navigate(`/reader/read/${b.id}`)}
            onShare={(s, at) => setSharing({ productId: s, finishedAt: at })}
          />
        ))}
      </div>
      {sharing && <FinishedDialog productId={sharing.productId} finishedAt={sharing.finishedAt} completionist={!!sharing.completionist} name={userName} onClose={() => setSharing(null)} />}
      <p className="mt-10 text-soft text-xs font-mono-ui leading-relaxed">
        One book can be open at a time. Opening another closes the current one, and your place is always saved.
      </p>
    </div>
  );
}
